import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { endpoints } from "../apiConfig";
import { colors, fonts, space, radius, buttonPrimary, buttonSecondary, inputBase, card, pageHeading } from "../theme";
import HelpButton from "../components/HelpButton";

const pad = (n) => String(n).padStart(2, "0");
const toDateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return toDateStr(d); };
const today = () => toDateStr(new Date());

export default function ReportsPage() {
  const [orders, setOrders] = useState([]);
  const [totals, setTotals] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [amount, setAmount] = useState("");
  const [recording, setRecording] = useState(false);
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(today());
  const [orderType, setOrderType] = useState("both");
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvColumns, setCsvColumns] = useState(['id', 'sales', 'tax', 'gratuity', 'tip', 'total_amount', 'status', 'created_at']);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  const load = (f = from, t = to, ot = orderType, ps = pageSize, cp = 1) => {
    if (!localStorage.getItem("token")) {
      window.location.href = "/login";
      return;
    }
    axios.get(endpoints.reports, { params: { from: f, to: t, order_type: ot, limit: ps, page: cp }, headers: authHeader() })
      .then((res) => { 
        setOrders(res.data.orders); 
        setTotals(res.data.totals);
        setTotalCount(res.data.total_count || 0);
        setTotalPages(res.data.total_pages || 1);
        setCurrentPage(res.data.page || 1);
      })
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 403) {
          window.location.href = "/login";
        }
      });

    const tenant = JSON.parse(localStorage.getItem("tenant") || "null");
    if (tenant) setBusinessName(tenant.business_name);
  };

  useEffect(() => load(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyRange = (e) => { e.preventDefault(); setCurrentPage(1); load(from, to, orderType, pageSize, 1); };
  const quickRange = (f, t) => { setFrom(f); setTo(t); setCurrentPage(1); load(f, t, orderType, pageSize, 1); };
  const handleOrderTypeChange = (newType) => { setOrderType(newType); setCurrentPage(1); load(from, to, newType, pageSize, 1); };
  const handlePageSizeChange = (newSize) => { setPageSize(newSize); setCurrentPage(1); load(from, to, orderType, newSize, 1); };
  const handlePrevPage = () => { const newPage = Math.max(1, currentPage - 1); setCurrentPage(newPage); load(from, to, orderType, pageSize, newPage); };
  const handleNextPage = () => { const newPage = Math.min(totalPages, currentPage + 1); setCurrentPage(newPage); load(from, to, orderType, pageSize, newPage); };

  const recordOrder = async (e) => {
    e.preventDefault();
    setRecording(true);
    try {
      await axios.post(
        endpoints.orders,
        { total_amount: parseFloat(amount) || 0, status: "paid" },
        { headers: authHeader() }
      );
      setAmount("");
      load();
    } finally {
      setRecording(false);
    }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("token");
    const columnsParam = encodeURIComponent(JSON.stringify(csvColumns));
    window.open(`${endpoints.reportsExport}?auth=${encodeURIComponent(token)}&from=${from}&to=${to}&order_type=${orderType}&columns=${columnsParam}`, "_blank");
  };
  
  const toggleCsvColumn = (col) => {
    setCsvColumns(prev => 
      prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]
    );
  };

  return (
    <div style={{ display: "flex", background: colors.bgPage, minHeight: "100vh" }}>
      <Sidebar businessName={businessName} />
      <div className="app-content" style={{ flex: 1, padding: space[6] }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: space[2] }}>
          <h1 style={pageHeading}>Reports <HelpButton page="reports" /></h1>
          <button onClick={() => setShowCsvModal(true)} style={buttonSecondary}>Export CSV</button>
        </div>
        
        {showCsvModal && (
          <div style={{ ...card, marginTop: space[4], background: colors.surfaceMuted, borderRadius: radius.md }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: space[3] }}>
              <div style={pageHeading}>Select columns to export</div>
              <button onClick={() => setShowCsvModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: colors.textSecondary }}>✕</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: space[2], marginBottom: space[3] }}>
              {['id', 'sales', 'tax', 'gratuity', 'tip', 'total_amount', 'status', 'created_at', 'order_type'].map(col => (
                <label key={col} style={{ display: "flex", gap: space[1], alignItems: "center", fontFamily: fonts.body, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={csvColumns.includes(col)} onChange={() => toggleCsvColumn(col)} />
                  {col.replace(/_/g, ' ')}
                </label>
              ))}
            </div>
            <div style={{ display: "flex", gap: space[2] }}>
              <button onClick={exportCsv} style={buttonPrimary}>Download CSV</button>
              <button onClick={() => setShowCsvModal(false)} style={buttonSecondary}>Cancel</button>
            </div>
          </div>
        )}

        <div style={{ ...card, marginTop: space[4], background: colors.surfaceMuted }}>
          <div style={{ display: "flex", gap: space[3], flexWrap: "wrap", alignItems: "center", marginBottom: space[3] }}>
            <div style={{ display: "flex", gap: space[1] }}>
              {['both', 'dine_in', 'online'].map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleOrderTypeChange(type)}
                  style={{
                    ...buttonSecondary,
                    background: orderType === type ? colors.brandBlue : "transparent",
                    color: orderType === type ? "#fff" : colors.textPrimary,
                    padding: "8px 12px",
                    fontSize: 13
                  }}
                >
                  {type === 'both' ? 'Both' : type === 'dine_in' ? 'Dine In' : 'Online'}
                </button>
              ))}
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: space[1], fontFamily: fonts.body, fontSize: 13, color: colors.textSecondary }}>
              Rows per page:
              <select value={pageSize} onChange={(e) => handlePageSizeChange(parseInt(e.target.value))} style={{ ...inputBase, width: 80, padding: "6px 8px" }}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
          </div>
        </div>

        <form onSubmit={applyRange} style={styles.rangeRow} data-testid="date-range-form">
          <label style={styles.rangeLabel}>From<input style={inputBase} type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From date" /></label>
          <label style={styles.rangeLabel}>To<input style={inputBase} type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To date" /></label>
          <button style={buttonPrimary} type="submit">Apply</button>
          <span style={styles.quickLinks}>
            <button type="button" style={styles.quickBtn} onClick={() => quickRange(today(), today())}>Today</button>
            <button type="button" style={styles.quickBtn} onClick={() => quickRange(firstOfMonth(), today())}>This month</button>
          </span>
        </form>

        {totals && (
          <div style={{ ...card, marginTop: space[4] }} data-testid="totals-summary">
            <div style={styles.totalsGrid}>
              <div style={styles.totalCell}><div style={styles.totalLabel}>Sales</div><div style={styles.totalValue}>${totals.subtotal.toFixed(2)}</div></div>
              <div style={styles.totalCell}><div style={styles.totalLabel}>Tax</div><div style={styles.totalValue}>${totals.tax.toFixed(2)}</div></div>
              <div style={styles.totalCell}><div style={styles.totalLabel}>Gratuity</div><div style={styles.totalValue}>${totals.gratuity.toFixed(2)}</div></div>
              <div style={styles.totalCell}><div style={styles.totalLabel}>Tip</div><div style={styles.totalValue}>${totals.tip.toFixed(2)}</div></div>
              <div style={styles.totalCell}><div style={styles.totalLabel}>All Total</div><div style={{ ...styles.totalValue, ...styles.grandTotal }}>${totals.total.toFixed(2)}</div></div>
            </div>
            {totals.orders_without_breakdown > 0 && (
              <p style={styles.breakdownNote} data-testid="breakdown-note">
                {totals.orders_without_breakdown} of {totals.orders} order{totals.orders === 1 ? "" : "s"} in this range closed before Sales/Tax/Gratuity/Tip tracking started —
                {" "}{totals.orders_without_breakdown === 1 ? "it's" : "they're"} counted in All Total but not in the breakdown above.
              </p>
            )}
          </div>
        )}

        <form className="inline-form" onSubmit={recordOrder} style={styles.recordForm}>
          <span style={styles.recordLabel}>Record a sample order:</span>
          <input
            style={styles.input}
            type="number"
            step="0.01"
            min="0"
            placeholder="Total amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
          <button style={buttonPrimary} type="submit" disabled={recording}>
            {recording ? "Recording…" : "Record Order"}
          </button>
        </form>
        <p style={styles.helperText}>
          There's no live waiter/kitchen ordering in this demo. Use this to generate
          sample order data so Reports and the Dashboard have something to show. A sample order has no
          Sales/Tax/Gratuity/Tip breakdown, only a total.
        </p>

        {orders.length === 0 ? (
          <p style={{ fontFamily: fonts.body, color: colors.textSecondary }}>No records in this date range.</p>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: `1px solid ${colors.border}` }}>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Sales</th>
                  <th style={styles.th}>Tax</th>
                  <th style={styles.th}>Gratuity</th>
                  <th style={styles.th}>Tip</th>
                  <th style={styles.th}>Total</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={styles.td}>{o.id}</td>
                    <td style={styles.td}>{o.subtotal === null ? "—" : `$${Number(o.subtotal).toFixed(2)}`}</td>
                    <td style={styles.td}>{o.tax_amount === null ? "—" : `$${Number(o.tax_amount).toFixed(2)}`}</td>
                    <td style={styles.td}>{o.gratuity_amount === null ? "—" : `$${Number(o.gratuity_amount).toFixed(2)}`}</td>
                    <td style={styles.td}>{o.tip_amount === null ? "—" : `$${Number(o.tip_amount).toFixed(2)}`}</td>
                    <td style={styles.td}>${Number(o.total_amount).toFixed(2)}</td>
                    <td style={styles.td}>{o.status}</td>
                    <td style={styles.td}>{new Date(o.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: space[4], fontFamily: fonts.body, fontSize: 13, color: colors.textSecondary }}>
              <span>Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)}–{Math.min(currentPage * pageSize, totalCount)} of {totalCount}</span>
              <div style={{ display: "flex", gap: space[2] }}>
                <button onClick={handlePrevPage} disabled={currentPage === 1} style={{ ...buttonSecondary, opacity: currentPage === 1 ? 0.5 : 1 }}>Previous</button>
                <button onClick={handleNextPage} disabled={currentPage >= totalPages} style={{ ...buttonSecondary, opacity: currentPage >= totalPages ? 0.5 : 1 }}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  rangeRow: { display: "flex", alignItems: "flex-end", gap: space[3], marginTop: space[4], flexWrap: "wrap" },
  rangeLabel: { display: "flex", flexDirection: "column", gap: 4, fontFamily: fonts.body, fontSize: 12, color: colors.textSecondary },
  quickLinks: { display: "flex", gap: space[2], marginLeft: space[2] },
  quickBtn: { ...buttonSecondary, padding: "8px 12px", fontSize: 13 },
  totalsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: space[3] },
  totalCell: { textAlign: "left" },
  totalLabel: { fontFamily: fonts.body, fontSize: 12, color: colors.textMuted, marginBottom: 2 },
  totalValue: { fontFamily: fonts.display, fontSize: 20, fontWeight: 700, color: colors.textPrimary },
  grandTotal: { color: colors.brandBlue },
  breakdownNote: { fontFamily: fonts.body, fontSize: 12, color: colors.textMuted, marginTop: space[3], marginBottom: 0 },
  recordForm: { display: "flex", alignItems: "center", gap: space[2], marginTop: space[5] },
  recordLabel: { fontFamily: fonts.body, fontSize: 13, color: colors.textSecondary, whiteSpace: "nowrap" },
  input: { ...inputBase, width: 140, padding: "8px 10px" },
  helperText: { fontFamily: fonts.body, fontSize: 12, color: colors.textMuted, marginTop: 6, marginBottom: space[4] },
  table: { width: "100%", borderCollapse: "collapse", marginTop: space[4], fontFamily: fonts.body },
  th: { padding: "8px 4px", fontSize: 13, color: colors.textSecondary },
  td: { padding: "8px 4px", fontSize: 14, color: colors.textPrimary },
};
